import {
  getDashboardCategories,
  RISK_THRESHOLDS,
  SAFETY_INDEX_THRESHOLDS,
} from "../constants/index";

const categoriesConfig = getDashboardCategories();

export const calculateProductAverageRisk = (product) => {
  if (!product?.stakingRiskClassifications?.length) return 0;

  let totalWeightedSum = 0;
  let totalWeight = 0;

  product.stakingRiskClassifications.forEach((classification) => {
    const weight = parseFloat(classification.totalScore) || 0;
    const risk =
      parseFloat(classification.manualRiskScore) ||
      parseFloat(classification.automaticRiskScore) ||
      0;

    totalWeightedSum += weight * risk;
    totalWeight += weight;
  });

  return totalWeight > 0 ? totalWeightedSum / totalWeight : 0;
};

export const calculateTotalWeightedScore = (product) => {
  if (!product) return 0;

  let totalWeightedScore = 0;

  categoriesConfig.forEach((category) => {
    const catData = product[category.key];
    if (
      catData &&
      typeof catData.totalScorePercentage === "number" &&
      typeof catData.riskScore === "number"
    ) {
      const weightedScore = catData.totalScorePercentage * catData.riskScore;
      totalWeightedScore += weightedScore;
    }
  });

  return totalWeightedScore;
};

export const calculateProductSafetyIndex = (product) => {
  const totalWeightedScore = calculateTotalWeightedScore(product);
  if (totalWeightedScore === 0) return -1;

  const safetyIndex = 100 - ((totalWeightedScore - 100) / (300 - 100)) * 100;
  return Math.max(0, Math.min(100, safetyIndex));
};

export const getSafetyIndexInfo = (index) => {
  if (index < 0) {
    return {
      category: "N/A",
      color: "text-gray-500",
      bgColor: "bg-gray-100 dark:bg-gray-700",
      borderColor: "border-gray-200 dark:border-gray-700",
    };
  }
  if (index < SAFETY_INDEX_THRESHOLDS.HIGH_RISK) {
    return {
      category: "High Risk",
      color: "text-red-500 dark:text-red-400",
      bgColor: "bg-red-100 dark:bg-red-900/30",
      borderColor: "border-red-500",
    };
  }
  if (index < SAFETY_INDEX_THRESHOLDS.MEDIUM_RISK) {
    return {
      category: "Medium Risk",
      color: "text-yellow-500 dark:text-yellow-400",
      bgColor: "bg-yellow-100 dark:bg-yellow-900/30",
      borderColor: "border-yellow-500",
    };
  }
  return {
    category: "Low Risk",
    color: "text-green-500 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900/30",
    borderColor: "border-green-500",
  };
};

export const getProductCompletionStats = (product) => {
  const classifications = product.stakingRiskClassifications || [];

  const { completed, total } = categoriesConfig.reduce(
    (acc, category) => {
      const classification = classifications.find(
        (c) => c.key === category.key,
      );
      if (
        classification &&
        parseFloat(classification.totalScore) > 0 &&
        parseFloat(classification.automaticRiskScore) > 0
      ) {
        acc.completed += 1;
      }
      acc.total += 1;
      return acc;
    },
    { completed: 0, total: 0 },
  );

  const completionPercentage = total > 0 ? (completed / total) * 100 : 0;
  return { completed, total, completionPercentage };
};

export const getRiskCategoryForScore = (score) => {
  if (score === 0) return "unassessed";
  if (score >= RISK_THRESHOLDS.HIGH_THRESHOLD) return "high";
  if (score >= RISK_THRESHOLDS.MEDIUM_THRESHOLD) return "medium";
  return "low";
};
